import React from "react";
import { Link } from "react-router-dom";
import { Logo } from "../components/Logo";
import { APP_URL, GOOGLE_REVIEW_URL } from "../lib/constants";

/**
 * Site footer.
 *
 * Structure:
 *   [ Brand + trademark disclaimer ]
 *   [ Product ] [ Company ] [ Support ] [ Legal ]
 *   [ Copyright ] [ Status ]
 *
 * Legal/marketing/utility routes render as real React Router links so they
 * work from every page (Landing, /app, /admin). Section anchors like #pricing
 * only make sense on the marketing site and stay as anchor hrefs.
 */
export const Footer = () => {
  const year = new Date().getFullYear();
  return (
    <footer
      data-testid="site-footer"
      className="border-t border-white/10 bg-black"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-10">
          {/* Brand + trademark block */}
          <div className="col-span-2 md:col-span-2">
            <Logo size={32} />
            <p className="text-zinc-500 text-sm mt-5 leading-relaxed max-w-xs">
              Independent battery intelligence for every EV owner. Predictive
              health, warranty math, and AI-powered optimization.
            </p>
            <p className="text-zinc-600 text-xs mt-5 font-mono leading-relaxed">
              EVIQ is an independent application that utilizes the Tesla Fleet
              API after owner authorization. EVIQ is not affiliated with,
              endorsed by, or sponsored by Tesla, Inc. Tesla®, Model S®, Model
              3®, Model X®, Model Y®, Cybertruck®, and related marks are
              trademarks of Tesla, Inc. Ford, Rivian, GM, Hyundai, Kia, Polestar,
              and Volvo are trademarks of their respective owners.
            </p>
          </div>

          <FooterCol
            title="Product"
            links={[
              { label: "Features", href: "/#features", section: true },
              { label: "Live demo", href: "/#demo", section: true },
              { label: "Pricing", href: "/#pricing", section: true },
              { label: "Open app", href: APP_URL, route: true },
              { label: "Why EVIQ?", href: "/why-eviq", route: true },
            ]}
          />
          <FooterCol
            title="Company"
            links={[
              { label: "About EVIQ", href: "/about", route: true },
              { label: "Why EVIQ?", href: "/why-eviq", route: true },
              { label: "Owner reviews", href: "/reviews", route: true },
              { label: "Blog", href: "/blog", route: true },
              { label: "EVIQ vs Teslita", href: "/vs/teslita", route: true },
              { label: "EVIQ vs Recurrent", href: "/vs/recurrent", route: true },
              { label: "Security", href: "/security", route: true },
              { label: "Contact", href: "/contact", route: true },
              ...(GOOGLE_REVIEW_URL
                ? [{ label: "Review us on Google", href: GOOGLE_REVIEW_URL, external: true }]
                : []),
            ]}
          />
          <FooterCol
            title="Support"
            links={[
              { label: "Contact", href: "/contact", route: true },
              { label: "FAQ", href: "/#faq", section: true },
              { label: "How it works", href: "/#how-it-works", section: true },
              {
                label: "support@eviq.pro",
                href: "mailto:support@eviq.pro",
                external: true,
              },
            ]}
          />
          <FooterCol
            title="Legal"
            links={[
              { label: "Privacy Policy", href: "/privacy", route: true },
              { label: "Terms of Service", href: "/terms", route: true },
              { label: "Cookie Policy", href: "/cookies", route: true },
              { label: "Security", href: "/security", route: true },
            ]}
          />
        </div>

        <div className="mt-14 pt-8 border-t border-white/5 flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-4">
          <div className="text-xs text-zinc-600 font-mono">
            © {year} EVIQ LLC · All rights reserved
          </div>
          <div className="flex items-center gap-2">
            <span className="relative flex h-1.5 w-1.5">
              <span
                className="absolute inline-flex h-full w-full rounded-full pulse-dot"
                style={{ background: "#39FF14" }}
              />
              <span
                className="relative inline-flex rounded-full h-1.5 w-1.5"
                style={{ background: "#39FF14" }}
              />
            </span>
            <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-zinc-400">
              All systems operational
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

const FooterCol = ({ title, links }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.22em] font-mono text-zinc-500 mb-5">
      {title}
    </div>
    <ul className="space-y-3">
      {links.map((l) => (
        <li key={l.label}>
          <FooterLink link={l} />
        </li>
      ))}
    </ul>
  </div>
);

const FooterLink = ({ link }) => {
  const testid = `footer-link-${link.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  const cls = "text-sm text-zinc-400 hover:text-white transition-colors";

  // React Router route (works from any page)
  if (link.route) {
    return (
      <Link to={link.href} data-testid={testid} className={cls}>
        {link.label}
      </Link>
    );
  }
  // Marketing section anchor (only meaningful on the landing page). Use a
  // full-path anchor so it also works from /about, /privacy, etc.
  if (link.section) {
    return (
      <a href={link.href} data-testid={testid} className={cls}>
        {link.label}
      </a>
    );
  }
  // External / mailto
  return (
    <a
      href={link.href}
      target={link.external && !link.href.startsWith("mailto:") ? "_blank" : undefined}
      rel={link.external && !link.href.startsWith("mailto:") ? "noopener noreferrer" : undefined}
      data-testid={testid}
      className={cls}
    >
      {link.label}
    </a>
  );
};
